import axios from 'axios';
import { contentTypeCatalogId, getEndpointContentType } from 'utils/endpoint';
import { HTTP_RESPONSE_UNAUTHORIZED } from 'utils/enums/http';
import { stringIsNullOrEmpty } from 'utils/propertyValidation';
import envVars from 'env/config';
import _ from 'lodash';
import { getAccessAuthToken, refreshAccessToken } from './auth';

/**
 * @import { AxiosError, AxiosRequestConfig } from 'axios';
 */

const MAX_ATTEMPT_REFRESH_ACCESS_TOKEN = 5;

let source = axios.CancelToken.source();

// if this is a powerBI request then use that token, else use session
const checkForToken = (headers) => {
  return headers !== null && headers.powerBIToken === undefined
    ? getAccessAuthToken()
    : headers.powerBIToken;
};

/**
 * requestHandler
 * Method to handle request data
 * @template T
 * @param {AxiosRequestConfig<T>} request data
 * @return {AxiosRequestConfig<T> | null} request data updated
 */
const requestHandler = (request) => {
  /**
   * This would've ideally been handled with separate axios instances, but that's not the way this was setup, so this function applies
   * globally to all axios calls. We don't want to add authorization headers to external requests.
   */
  if (!_.startsWith(request.url, envVars.domainName)) {
    return request;
  }

  const token = request !== null ? checkForToken(request.headers) : null;

  if (token) {
    request.headers.authorization = `Bearer ${token}`;
    const requestContentType = getEndpointContentType(request.url);
    switch (requestContentType.typeId) {
      case contentTypeCatalogId.multiPart: {
        const isFormData = request.data && typeof request.data.append === 'function';
        if (isFormData) {
          // Let the browser set the multipart boundary.
          delete request.headers['Content-Type'];
        } else {
          request.headers['Content-Type'] = requestContentType.value;
        }
        break;
      }
      default:
        // nothing to do
        break;
    }
    return request;
  }
  return request;
};

/**
 * successHandler
 * Method to handle success response from http request
 * @param {object} response success response data
 * @return {object} response data
 */
const successHandler = (response) => response?.data || response;

/**
 * errorHandler
 * Method to to handle error response from http request
 * @template T
 * @template D
 * @param {AxiosError<T, D>} error response data
 * @throws {AxiosError<T, D>}
 */
const errorHandler = async (error) => {
  const { response } = error;
  const { status } = response || {};

  /** @type {AxiosRequestConfig<D> & { refreshTokenAttempt?: number}} */
  const config = response?.config || {};

  /**
   * This would've ideally been handled with separate axios instances, but that's not the way this was setup, so this function applies
   * globally to all axios calls. We don't want to attempt token refresh for external requests.
   */
  if (!_.startsWith(config.url, envVars.domainName)) {
    throw error;
  }

  if (status !== HTTP_RESPONSE_UNAUTHORIZED) {
    throw error;
  }

  // If we get unauthorized response we need to get a refresh access token and re-call the http request
  config.refreshTokenAttempt = config.refreshTokenAttempt || 0;

  if (config.refreshTokenAttempt < MAX_ATTEMPT_REFRESH_ACCESS_TOKEN) {
    config.refreshTokenAttempt += 1;

    // Refresh access tokens when unauthorized code status is returned
    await refreshAccessToken();
    return axios.request(config);
  }

  throw error;
};

/**
 * initializeHttpInterceptors
 * Method to subscribe to axios interceptors
 */
export const initializeHttpInterceptors = () => {
  // Handle request process
  axios.interceptors.request.use((request) => requestHandler(request));

  // Handle response process
  axios.interceptors.response.use(
    (response) => successHandler(response),
    (error) => errorHandler(error),
  );
};

/**
 * doGet
 * Method to call GET request
 * @param {string} url string for http  request
 * @param {string} responseType string for type of response expected
 * @return {object} api promise
 */
export const doGet = (url, responseType, params = {}) => {
  if (stringIsNullOrEmpty(url)) {
    return Promise.reject(new Error('API url is empty'));
  }

  if (responseType) {
    return axios.get(url, { responseType });
  }
  return axios.get(url, { params });
};

/**
 * doPost
 * Method to call POST request
 * @param {string} url string for http  request
 * @param {object} requestPayload Object to update
 * @param {object} config extra config
 * @return {object} api promise
 */
export const doPost = (url, requestPayload, config = {}) => {
  if (stringIsNullOrEmpty(url)) {
    return Promise.reject(new Error('API url is empty'));
  }
  return axios.post(url, requestPayload, {
    ...config,
    cancelToken: source?.token,
  });
};

export const doCancel = () => {
  source.cancel('Cancel Request');
  // Renew the token after the previous one has been used to prevent further requests to fail
  source = axios.CancelToken.source();
};

/**
 * doPut
 * Method to call PUT request
 * @param {string} url string for http  request
 * @param {object} requestPayload Object to update
 * @param {object} config Optional configuration object, including headers
 * @return {object} api promise
 */
export const doPut = (url, requestPayload, config = {}) => {
  if (stringIsNullOrEmpty(url)) {
    return Promise.reject(new Error('API url is empty'));
  }

  return axios.put(url, requestPayload, config);
};

/**
 * doPatch
 * Method to call PATCH request
 * @param {string} url string for http  request
 * @param {object} requestPayload Object to update
 * @return {object} api promise
 */
export const doPatch = (url, requestPayload) => {
  if (stringIsNullOrEmpty(url)) {
    return Promise.reject(new Error('API url is empty'));
  }

  return axios.patch(url, requestPayload);
};

/**
 * doDelete
 * Method to call DELETE request
 * @param {string} url string for http request
 * @param {object} config Optional configuration object, including data for the request body
 * @return {object} api promise
 */
export const doDelete = (url, config = {}) => {
  if (stringIsNullOrEmpty(url)) {
    return Promise.reject(new Error('API url is empty'));
  }

  return axios.delete(url, config);
};
